import { intersection, uniq } from 'ramda';
import type { Location, Range } from 'slate';
import { Editor, Text } from 'slate';

import type { UUID } from '../../types/type-helpers';
import { withIndex } from '../../util';
import type { AlineText } from '../internal';
import { getIsTextNodeWithComments, Queries } from '../internal';

export type CommentLengthTuple = [string, number];
export type CommentIdToRanges = Record<string, { ranges: Range[] }>;
export type CommentRanges = { commentRanges: CommentIdToRanges; uniqCommentIds: string[] };

const commentQueries = {
  /**
   * Returns the ID of the active comment at the current selection, if any.
   */
  getActiveCommentId: (editor: Editor): UUID | null => {
    const selectedCommentIds = Queries.getSelectedCommentIds(editor);

    return Queries.getShortestCommentId(editor, { commentIds: selectedCommentIds });
  },

  /**
   * Gets all the unique commentIds that are in the documentJSON.
   */
  getCommentIdsInJSON: (editor: Editor): UUID[] => {
    const activeRootEntries = Queries.getActiveRootEntries(editor);
    const entries = activeRootEntries.flatMap(([, path]) => [
      ...Editor.nodes<AlineText>(editor, {
        at: path,
        match: node => getIsTextNodeWithComments(node),
        voids: true,
      }),
    ]);

    return uniq(entries.flatMap(([text]) => text.comments ?? []));
  },
  /**
   * Gets a Record of commentId to the ranges of that comment in the document.
   */
  getCommentRanges: (editor: Editor, options: { at: Location }): CommentRanges => {
    const { at } = options;
    const textEntriesWithComments = [
      ...Editor.nodes<AlineText>(editor, {
        at,
        match: getIsTextNodeWithComments,
        mode: 'all',
        voids: true,
      }),
    ];
    const uniqCommentIds = uniq(textEntriesWithComments.flatMap(([text]) => text.comments ?? []));
    const commentRanges = textEntriesWithComments.reduce<CommentIdToRanges>(
      (acc, [textNode, textPath]) => {
        return (textNode.comments ?? []).reduce<CommentIdToRanges>((innerAcc, commentId) => {
          const { ranges = [] } = innerAcc[commentId] ?? {};
          if (!ranges.length) {
            innerAcc[commentId] = {
              ranges: [
                {
                  anchor: Editor.start(editor, textPath),
                  focus: Editor.end(editor, textPath),
                },
              ],
            };
            return innerAcc;
          }

          const { newRanges } = ranges.reduce<{ added: boolean; newRanges: Range[] }>(
            (rangeAcc, range, index) => {
              const { added, newRanges } = rangeAcc;
              const {
                focus: { path: focusPath },
              } = range;
              const isLast = index === ranges.length - 1;
              const safeAdd: Range[] =
                isLast && !added
                  ? [
                      {
                        anchor: Editor.start(editor, textPath),
                        focus: Editor.end(editor, textPath),
                      },
                    ]
                  : [];
              const isFocusPathAbuttingText = Queries.pathIsAbutting(editor, focusPath, textPath);
              if (isFocusPathAbuttingText)
                return {
                  added: true,
                  newRanges: [...newRanges, { ...range, focus: Editor.end(editor, textPath) }],
                };

              rangeAcc.newRanges.push(range, ...safeAdd);
              return rangeAcc;
            },
            { added: false, newRanges: [] }
          );

          innerAcc[commentId] = { ranges: newRanges };
          return innerAcc;
        }, acc);
      },
      {}
    );
    return { commentRanges, uniqCommentIds };
  },
  /**
   * Returns the IDs of the selected comments at the provided Range.
   */
  getSelectedCommentIds: (editor: Editor, options: { at?: Range } = {}): UUID[] => {
    const { at = editor.selection } = options;
    if (!at) return [];

    const textNodeEntries = Editor.nodes<AlineText>(editor, {
      at,
      match: Text.isText,
      mode: 'all',
      voids: true,
    });
    const commentIntersection: { intersectingIds: string[] } = { intersectingIds: [] };

    for (const [[textNode], index] of withIndex(textNodeEntries)) {
      if (!textNode?.text?.length) continue;
      if (index === 0) {
        commentIntersection.intersectingIds = textNode.comments ?? [];
        continue;
      }

      commentIntersection.intersectingIds = intersection(
        commentIntersection.intersectingIds,
        textNode.comments ?? []
      );

      if (!commentIntersection.intersectingIds.length) break;
    }

    return commentIntersection.intersectingIds;
  },

  /**
   * Returns the "shortest" commentId from a list of commentIds provided.  Helpful in determining the "active" or "selected"
   * comment when the selection spans multiple comments
   */
  getShortestCommentId: (editor: Editor, options: { commentIds: string[] }): UUID | null => {
    const { commentIds } = options;
    const commentTuples = commentIds.map((id: string): CommentLengthTuple => {
      const textNodeEntries = [
        ...Editor.nodes<AlineText>(editor, {
          at: [],
          match: node =>
            Text.isText(node) && Array.isArray(node.comments) && node.comments.includes(id),
          voids: true,
        }),
      ];
      const commentTuple: [string, number] = [id, 0];

      textNodeEntries.forEach(([textNode]) => {
        commentTuple[1] += textNode.text.length;
      });

      return commentTuple;
    });
    const commentTuplesWithShortestFirst = commentTuples.sort((a, b) => {
      const [, lengthA] = a;
      const [, lengthB] = b;

      return lengthA - lengthB;
    });
    if (!commentTuplesWithShortestFirst.length) return null;

    const [[shortestCommentId]] = commentTuplesWithShortestFirst;

    return shortestCommentId ?? null;
  },

  /**
   * Determines whether nodes have intersecting comments.
   */
  textNodesHaveIntersectingComments(textA: AlineText, textB: AlineText): boolean {
    const textACommentIds = textA.comments ?? [];
    const textBCommentIds = textB.comments ?? [];
    const intersectingIds = intersection(textACommentIds, textBCommentIds);
    return Boolean(intersectingIds.length);
  },
};

export default commentQueries;
